import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Search, MessageSquare, UserPlus, Heart, LogOut, ShieldCheck, Crown, Mail, UserCog, ScrollText, Sparkles, Share2, Users, Home, Network, Image as ImageIcon, Activity, HelpCircle, BookOpen, KeyRound } from 'lucide-react';





import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useMembership } from '@/contexts/MembershipContext';
import { useT } from '@/contexts/LanguageContext';
import { openSupportEmail } from '@/lib/support';
import { useCommandCenterAuthority } from '@/hooks/useCommandCenterAuthority';
import InviteModal from './InviteModal';
import SharePublicModal from './SharePublicModal';
import LanguageSelector from './LanguageSelector';
import SystemStatusPanel from './SystemStatusPanel';



interface HeaderProps {
  onNavigate: (section: string) => void;
  onOpenMessages?: () => void;
  unreadCount?: number;
}

const Logo: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => (
  <div className="flex items-center gap-3">
    <div className={`relative ${size === 'sm' ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl bg-white flex items-center justify-center shadow-md overflow-hidden`}>
      <div className="absolute left-1.5 top-1.5 w-2.5 h-2.5 rounded-xl bg-purple-400/90" />
      <div className="absolute right-1.5 top-1.5 w-2.5 h-2.5 rounded-xl bg-orange-300" />
      <img src="/brand/kinscircle-tree-icon.png" alt="KinsCircle" className="w-full h-full object-contain rounded-xl scale-105" />
    </div>
    <div className="text-left">
      <div className={`font-serif font-bold leading-none ${size === 'sm' ? 'text-lg' : 'text-xl md:text-2xl'}`}>
        <span className="text-slate-900">Kins</span>
        <span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">Circle</span>
      </div>
      <div className="text-xs text-slate-600 tracking-wide mt-0.5">Bringing Families Together</div>
    </div>
  </div>
);

export { Logo };

const Header: React.FC<HeaderProps> = ({ onNavigate, onOpenMessages, unreadCount = 0 }) => {
  const navigate = useNavigate();
  const t = useT();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  
  const {
    isMember,
    isPaid,
    isAdmin,
    isOwner,
    isAuthenticated,
    plan,
    openMembershipModal,
    openAuthModal,
    signOut,
  } = useMembership();

  const commandCenter =
    useCommandCenterAuthority(
      isAuthenticated,
    );


  const links = [
    { id: 'home', label: t('Home') },
    { id: 'tree', label: t('Features') },
    { id: 'pricing', label: t('Pricing') },
    { id: 'compare', label: t('About') },
    { id: 'news', label: t('Blog') },
    // 'contact' is a sentinel — handleClick intercepts it and opens the
    // user's mail app instead of scrolling to a section. The support
    // address itself is intentionally NOT rendered anywhere in the UI.
    { id: 'contact', label: t('Contact Support') },
  ];


  // Hide the "Pricing" nav link for paid + admin users — they shouldn't be
  // upsold to a plan they already have.
  const visibleLinks = isPaid || isAdmin ? links.filter((l) => l.id !== 'pricing') : links;

  const handleClick = (id: string) => {
    if (id === 'contact') {
      openSupportEmail();
      setMobileOpen(false);
      return;
    }
    onNavigate(id);
    setMobileOpen(false);
  };


  const handleMessages = () => {
    if (onOpenMessages) onOpenMessages();
    setMobileOpen(false);
  };

  React.useEffect(() => {
    const openMessages = () => {
      setMessagesPreviewOpen(true);
      setMobileOpen(false);
    };

    window.addEventListener('kinscircle:open-messages', openMessages);

    return () => {
      window.removeEventListener('kinscircle:open-messages', openMessages);
    };
  }, []);

  const handleCommandCenter = () => {
    setMobileOpen(false);

    if (!commandCenter.allowed) {
      return;
    }

    navigate('/governor-command-center');
  };

  const handleInvite = () => {
    setInviteOpen(true);
    setMobileOpen(false);
  };

  const handleLogIn = () => {
    openAuthModal('signin');
    setMobileOpen(false);
  };

  const handleSignUp = () => {
    openAuthModal('signup');
    setMobileOpen(false);
  };

  const handleGetStarted = () => {
    openMembershipModal();
    setMobileOpen(false);
  };
  const handleLogOut = async () => {
    setMobileOpen(false);

    try {
      await signOut();
      toast.success(
        'You have been signed out.',
      );

      window.location.replace('/login');
    } catch (unknownError: unknown) {
      const message =
        unknownError instanceof Error
          ? unknownError.message
          : 'Logout failed. Please try again.';

      console.error(
        '[Header] verified logout failed',
        unknownError,
      );

      toast.error(message);
    }
  };

  // Open the in-app Guide modal. Mounted once at the AppLayout level and
  // listens for this window event so any surface can pop it without prop
  // drilling. Visible to ALL users (auth + guest) — see top header bar
  // and the mobile menu entry below.
  const handleOpenGuide = () => {
    setMobileOpen(false);
    try {
      window.dispatchEvent(new Event('kc:open-help'));
    } catch {
      /* ignore */
    }
  };

  // Open the in-app Guide modal directly to the searchable Feature Guide /
  // Help Center tab. Used by the hamburger "Feature Guide" menu item so a
  // single tap takes the user from anywhere in the app straight into the
  // full feature index. Reuses the same global event channel so we don't
  // need to thread a ref or context through the tree.
  const handleOpenFeatureGuide = () => {
    setMobileOpen(false);
    try {
      window.dispatchEvent(
        new CustomEvent('kc:open-help', { detail: { tab: 'features' } }),
      );
    } catch {
      /* ignore */
    }
  };


  const planLabel =
    plan === 'annual_founding'
      ? 'Founding Member'
      : plan === 'monthly_premium'
      ? 'Premium'
      : null;

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() =>
                navigate(isAuthenticated ? '/app' : '/login')
              }
              aria-label="KinsCircle home"
            >
              <Logo />
            </button>

            <nav className="hidden lg:flex items-center gap-1">
              {visibleLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleClick(l.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-xl transition-colors ${
                    l.id === 'pricing'
                      ? 'text-fuchsia-600 hover:bg-fuchsia-50'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2">
              {isMember && (
                <>
                  <button
                    onClick={() => toast('Search coming soon — try clicking a family member!')}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={handleInvite}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                    aria-label="Invite family"
                    title="Invite family"
                  >
                    <UserPlus className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={handleMessages}
                    className="relative p-2 hover:bg-fuchsia-50 rounded-xl transition-all hover:scale-105 duration-200 shadow-sm hover:shadow-md"
                    aria-label="Messages"
                  >
                    <MessageSquare className="w-5 h-5 text-slate-700" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-xl bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center shadow">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                </>
              )}

              {/* Plan / role badges for paid + admin users */}
              {isAuthenticated && (isOwner || isAdmin) && (
                <span
                  className="hidden xl:inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-[11px] font-semibold"
                  title={isOwner ? 'Family owner — full access' : 'Admin — full access'}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {isOwner ? 'Owner' : 'Admin'}
                </span>
              )}
              {isAuthenticated && isPaid && planLabel && (
                <span
                  className="hidden xl:inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold"
                  title={`Plan: ${plan}`}
                >
                  <Crown className="w-3.5 h-3.5" />
                  {planLabel}
                </span>
              )}
              {/* Public "Guide / How it works" — visible to EVERYONE
                  (auth + guest) so help is always one click away at the
                  top of the screen, no scrolling needed. Dispatches a
                  global window event picked up by GuideModal. */}
              <Button
                onClick={handleOpenGuide}
                variant="outline"
                className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 px-4 gap-1.5"
                title={t('How it works', 'How it works')}
              >
                <HelpCircle className="w-4 h-4" />
                {t('Guide', 'Guide')}
              </Button>

              {/* Public "Share KinsCircle" — visible to EVERYONE (auth + guest)
                  so users can promote the platform on social media at any time. */}
              <Button
                onClick={() => setShareOpen(true)}
                variant="outline"
                className="rounded-xl border-fuchsia-300 text-[#8b2bd6] hover:bg-[#f8ecff] px-4 gap-1.5"
                title={t('Share KinsCircle')}
              >
                <Share2 className="w-4 h-4" />
                {t('Share KinsCircle')}
              </Button>


              {/* "Start Your Family" — public CTA, visible always so anyone
                  (logged in or not) can jump straight into the public /join
                  onboarding flow. */}
              <Button
                onClick={() => { setMobileOpen(false); navigate('/join'); }}
                className="rounded-xl bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white px-5 shadow-md gap-1.5"
                title={t('Start Your Family')}
              >
                <Users className="w-4 h-4" />
                {t('Start Your Family')}
              </Button>

              {/* Auth buttons: show Log In / Sign Up ONLY for guests.
                  Authenticated users see a Log Out button instead. */}
              {!isAuthenticated && (
                <>
                  <Button
                    onClick={handleLogIn}
                    variant="outline"
                    className="rounded-xl border-fuchsia-300 text-[#8b2bd6] hover:bg-[#f8ecff] px-5"
                  >
                    {t('Log In')}
                  </Button>
                  <Button
                    onClick={handleSignUp}
                    variant="outline"
                    className="rounded-xl border-slate-300 text-slate-700 hover:bg-[#fff7e6] px-5"
                  >
                    {t('Sign Up')}
                  </Button>
                </>
              )}



              {/* Authenticated free user: keep upgrade CTA + log out */}
              {isAuthenticated && !isPaid && !isAdmin && (
                <Button
                  onClick={handleGetStarted}
                  className="rounded-xl bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white px-5 shadow-md"
                >
                  {t('Upgrade')}
                </Button>
              )}
              {/* TEMPORARY — Owner-only "Admin Keys" shortcut.
                  Routes to /admin/keys (Secure Key Vault) so the family
                  owner can paste rotated secrets without exposing them in
                  chat. Visible ONLY when isOwner === true; safe to remove
                  once key rotation is complete. */}
              {isAuthenticated && isOwner && (
                <Button
                  onClick={() => navigate('/admin/keys')}
                  variant="outline"
                  className="rounded-xl border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 px-4 gap-1.5 font-semibold"
                  title="Owner-only: paste rotated API keys securely"
                >
                  <KeyRound className="w-4 h-4" />
                  Admin Keys
                </Button>
              )}
              {/* Email (SMTP) settings — always visible while DEV bypass is on
                  so the owner can configure delivery without signing in. */}
              <Button
                onClick={() => navigate('/settings/email')}
                variant="outline"
                className="rounded-xl border-fuchsia-300 text-[#8b2bd6] hover:bg-[#f8ecff] px-4 gap-1.5"
                title="Configure SMTP for password-reset & magic-link emails"
              >
                <Mail className="w-4 h-4" />
                {t('Email Settings')}
              </Button>


              {isAuthenticated &&
                commandCenter.allowed && (
                  <Button
                    onClick={handleCommandCenter}
                    variant="outline"
                    className="rounded-xl border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 px-4 gap-1.5 font-semibold"
                    title="Open governed Command Center"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Command Center
                  </Button>
                )}

              {isAuthenticated && (
                <Button
                  onClick={() => navigate('/settings')}
                  variant="outline"
                  className="rounded-xl border-slate-300 text-slate-700 hover:bg-[#fff7e6] px-4 gap-1.5"
                  title={t('Account settings')}
                >
                  <UserCog className="w-4 h-4" />
                  {t('Account')}
                </Button>
              )}

              {isAuthenticated && (
                <Button
                  onClick={handleLogOut}
                  variant="outline"
                  className="rounded-xl border-slate-300 text-slate-700 hover:bg-[#fff7e6] px-4 gap-1.5"
                  title={t('Sign out')}
                >
                  <LogOut className="w-4 h-4" />
                  {t('Log Out')}
                </Button>
              )}
            </div>

            <div className="lg:hidden flex items-center gap-1">

              {/* Guide / How it works — ALWAYS visible on the mobile header
                  bar (not hidden behind the hamburger menu) so help is one
                  tap away on any page, for any user (auth + guest). */}
              <button
                onClick={handleOpenGuide}
                className="p-2 hover:bg-amber-50 rounded-xl transition-colors"
                aria-label={t('Guide', 'Guide')}
                title={t('How it works', 'How it works')}
              >
                <HelpCircle className="w-5 h-5 text-amber-700" />
              </button>

              {isMember && (
                <button
                  onClick={handleMessages}
                  className="relative p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  aria-label="Messages"
                >
                  <MessageSquare className="w-5 h-5 text-slate-700" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-xl bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center shadow">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              )}
              {/* Sign In — ALWAYS visible on the mobile header bar (not
                  hidden behind the hamburger menu) so logged-out visitors
                  on phones can immediately tap to sign in from the homepage,
                  /join, or /invite. Hidden once the user is authenticated. */}
              {!isAuthenticated && (
                <Button
                  onClick={handleLogIn}
                  size="sm"
                  className="rounded-xl bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white px-3 h-8 text-xs font-semibold shadow"
                >
                  {t('Sign Up', 'Sign Up')}
                </Button>
              )}
              <button
                className="p-2"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Menu"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

          </div>


          {/* Desktop app navigation — visible only on lg+ for signed-in members.
              Provides quick access to the core app surfaces without forcing
              users into the hamburger menu. Mobile is unaffected. */}
          {isAuthenticated && (
            <nav className="hidden lg:flex items-center gap-1 py-2 border-t border-slate-100 overflow-x-auto">
              <button
                onClick={() => handleClick('home')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Home')}
              >
                <Home className="w-4 h-4" />
                {t('Home')}
              </button>
              <button
                onClick={() => handleClick('graph')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Family Tree')}
              >
                <Network className="w-4 h-4" />
                {t('Family Tree')}
              </button>
              <button
                onClick={() => handleClick('gallery')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Memories')}
              >
                <ImageIcon className="w-4 h-4" />
                {t('Memories')}
              </button>
              <button
                onClick={handleInvite}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Invite family')}
              >
                <UserPlus className="w-4 h-4" />
                {t('Invite Family')}
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Share KinsCircle')}
              >
                <Share2 className="w-4 h-4" />
                {t('Share KinsCircle')}
              </button>
              <button
                onClick={() => setStatusOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#0f9f8f] hover:bg-[#e9fffb] transition-colors ml-auto"
                title="View system status"
              >
                <Activity className="w-4 h-4" />
                {t('System Status', 'System Status')}
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-xl text-slate-700 hover:text-[#8b2bd6] hover:bg-[#f8ecff] transition-colors"
                title={t('Account settings')}
              >
                <UserCog className="w-4 h-4" />
                {t('Settings')}
              </button>
            </nav>
          )}



          {mobileOpen && (
            <>
              {/* Backdrop — closes the menu when tapped outside. Sits BELOW
                  the menu panel (z-40) but above page content. */}
              <div
                className="lg:hidden fixed inset-0 top-20 z-40 bg-slate-900/30 backdrop-blur-[1px]"
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
              />

              {/* Mobile menu panel — FIXED positioning below the sticky
                  header so it has its own independent scroll context.
                  Uses 100dvh (dynamic viewport) minus header height so it
                  always fits the visible area on iOS Safari / Chrome
                  mobile (where the URL bar shrinks/grows). overflow-y:auto
                  guarantees scroll for ALL roles (owner, admin, member,
                  invited member, guest) regardless of how many items are
                  rendered. pb-40 reserves space at the bottom so the
                  Logout button is never hidden behind the
                  "Built on Famous.ai" badge or device home indicator. */}
              <div
                className="lg:hidden fixed left-0 right-0 top-20 z-50 bg-white border-t border-slate-100 shadow-xl overflow-y-auto overscroll-contain"
                style={{
                  maxHeight: 'calc(100dvh - 5rem)',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="px-4 sm:px-6 py-3 space-y-1 pb-40">
              {/* "Start Your Family" — public CTA, visible to ALL users
                  (auth + guest), the FIRST thing in the mobile menu so it's
                  never missed. Routes to the public /join onboarding page. */}
              <button
                onClick={() => { setMobileOpen(false); navigate('/join'); }}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-xl bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 text-white font-semibold shadow"
              >
                <Users className="w-5 h-5" />
                {t('Start Your Family')}
              </button>

              {/* "Share KinsCircle" — public share modal, visible to ALL users
                  so they can promote the platform on social media. */}
              <button
                onClick={() => { setMobileOpen(false); setShareOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-xl border border-fuchsia-200 bg-white text-fuchsia-700 font-semibold"
              >
                <Share2 className="w-5 h-5" />
                {t('Share KinsCircle')}
              </button>

              {/* "Feature Guide" — primary hamburger entry. Opens the in-app
                  Help Center directly to the searchable Feature Guide tab.
                  Listed FIRST in the help section so users can find every
                  feature with one tap. Visible to ALL users (auth + guest). */}
              <button
                onClick={handleOpenFeatureGuide}
                className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-xl border border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-pink-50 text-fuchsia-700 font-semibold"
              >
                <BookOpen className="w-5 h-5" />
                {t('Feature Guide', 'Feature Guide')}
              </button>

              {/* "How it works" Guide — visible to ALL users so anyone can
                  learn how KinsCircle works without leaving the page. */}
              <button
                onClick={handleOpenGuide}
                className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-xl border border-amber-200 bg-amber-50/40 text-amber-800 font-semibold"
              >
                <HelpCircle className="w-5 h-5" />
                {t('Guide — How it works', 'Guide — How it works')}
              </button>



              {/* Language selector — placed at the TOP of the mobile menu so
                  users can switch the app's language without digging into
                  settings. Persists to localStorage (always) and to the
                  user's profile (if signed in). */}
              <div className="px-4 py-3 mb-2 rounded-xl border border-slate-200 bg-slate-50/60">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                  {t('Language')}
                </div>
                <LanguageSelector triggerClassName="w-full rounded-xl bg-white" />
              </div>

              {visibleLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleClick(l.id)}
                  className="w-full text-left flex items-center px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg"
                >
                  {l.label}
                </button>
              ))}
              {isMember && (
                <>
                  <button
                    onClick={handleMessages}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg"
                  >
                    
<div className="relative">
  <MessageSquare className="w-5 h-5" />
  <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-xl bg-fuchsia-500 px-1 text-[10px] font-bold text-white">
    3
  </span>
</div>

                    {t('Messages')}
                    {unreadCount > 0 && (
                      <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-xl bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={handleInvite}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg"
                  >
                    <UserPlus className="w-5 h-5" />
                    {t('Invite family')}
                  </button>
                </>
              )}


              {isAuthenticated && (isOwner || isAdmin || isPaid) && (
                <div className="px-4 py-2 flex flex-wrap gap-2">
                  {(isOwner || isAdmin) && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-[11px] font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {isOwner ? 'Owner' : 'Admin'}
                    </span>
                  )}
                  {isPaid && planLabel && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold">
                      <Crown className="w-3.5 h-3.5" />
                      {planLabel}
                    </span>
                  )}
                </div>
              )}

              {!isAuthenticated && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    onClick={handleLogIn}
                    variant="outline"
                    className="rounded-xl border-fuchsia-300 text-fuchsia-700"
                  >
                    {t('Log In')}
                  </Button>
                  <Button
                    onClick={handleSignUp}
                    className="rounded-xl bg-gradient-to-r from-[#8b2bd6] via-[#e23aa8] to-[#f6b73c] text-white"
                  >
                    {t('Get Started')}
                  </Button>
                </div>
              )}

              {isAuthenticated && !isPaid && !isAdmin && (
                <div className="pt-2">
                  <Button
                    onClick={handleGetStarted}
                    className="w-full rounded-xl bg-gradient-to-r from-[#8b2bd6] via-[#e23aa8] to-[#f6b73c] text-white"
                  >
                    {t('Upgrade')}
                  </Button>
                </div>
              )}

              {/* Email (SMTP) settings — always available in mobile menu while
                  DEV bypass is on, so the owner can configure delivery. */}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/settings/email');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-[#8b2bd6] hover:bg-[#f8ecff] rounded-lg font-semibold"
              >
                <Mail className="w-5 h-5" />
                {t('Email Settings (SMTP)')}
              </button>

              {/* TEMPORARY — Owner-only "Admin Keys" shortcut (mobile menu).
                  Mirrors the desktop button. Visible ONLY when isOwner === true. */}
              {isAuthenticated && isOwner && (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/admin/keys');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg font-semibold border border-amber-200"
                >
                  <KeyRound className="w-5 h-5" />
                  Admin Keys
                </button>
              )}

              {isAuthenticated &&
                commandCenter.allowed && (
                  <button
                    onClick={handleCommandCenter}
                    className="w-full flex items-center gap-3 px-4 py-3 text-violet-800 bg-violet-50 hover:bg-violet-100 rounded-lg font-semibold border border-violet-200"
                  >
                    <ShieldCheck className="w-5 h-5" />
                    Command Center
                  </button>
                )}

              {isAuthenticated && (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg font-semibold"
                >
                  <UserCog className="w-5 h-5" />
                  {t('Account settings')}
                </button>
              )}


              {/* System Status — visible to all users (auth + guest) so anyone
                  hitting an issue can quickly verify which subsystems are up. */}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  setStatusOpen(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-[#0f9f8f] hover:bg-[#e9fffb] rounded-lg font-semibold"
              >
                <Activity className="w-5 h-5" />
                {t('System Status', 'System Status')}
              </button>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/privacy');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg font-semibold"
              >
                <ShieldCheck className="w-5 h-5" />
                {t('Open Privacy & Safety', 'Privacy & Safety')}
              </button>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/terms');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-[#fff7e6] rounded-lg font-semibold"
              >
                <ScrollText className="w-5 h-5" />
                {t('Open Terms of Service', 'Terms of Service')}
              </button>


              {/* Log Out — ALWAYS rendered for ANY authenticated user
                  (owner, admin, member, invited member). Wrapped in extra
                  bottom margin so it sits above the device home indicator
                  and the "Built on Famous.ai" badge. */}
              {isAuthenticated && (
                <div className="pt-2 mt-2 border-t border-slate-100">
                  <Button
                    onClick={handleLogOut}
                    variant="outline"
                    className="w-full rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5 h-12"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('Log Out')}
                  </Button>
                </div>
              )}
                </div>
              </div>
            </>
          )}

        </div>
      </header>
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} />
      <SharePublicModal open={shareOpen} onOpenChange={setShareOpen} />
      <SystemStatusPanel open={statusOpen} onOpenChange={setStatusOpen} />

      
    </>

  );
};

export default Header;
